import type { FC, FormEvent, ReactNode } from 'react'

import Link from 'next/link'
import { useState } from 'react'
import { ArrowRight, Check, Loader2 } from 'lucide-react'
import { cn } from '@/lib/utils'

import { BUDGETS } from '@/constants/agency'

type FieldName = 'name' | 'email' | 'company' | 'budget' | 'services' | 'message'
type FieldErrors = Partial<Record<FieldName, string>>
type Status = 'idle' | 'submitting' | 'success' | 'error'

interface ContactFormProps {
    serviceOptions: string[]
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/
const ERROR = 'text-[#A3311B]'

const INITIAL = {
    name: '',
    email: '',
    company: '',
    budget: '',
    message: '',
    website: ''
}

const validate = (values: typeof INITIAL): FieldErrors => {
    const errors: FieldErrors = {}

    if (values.name.trim().length < 2) errors.name = 'Please enter your name.'
    if (!EMAIL_PATTERN.test(values.email.trim())) errors.email = 'Please enter a valid email address.'
    if (values.company.trim().length > 120) errors.company = 'Please keep the company name under 120 characters.'
    if (values.message.trim().length < 20) errors.message = 'Tell us a little more about the project, at least a couple of sentences.'
    if (values.message.trim().length > 4000) errors.message = 'Please keep your message under 4000 characters.'

    return errors
}

const LABEL = 'font-mono text-[11px] uppercase tracking-[0.18em] text-charcoal/80'

const inputClass = (invalid: boolean): string => cn(
    'mt-2 block w-full rounded-none border-0 border-b-2 bg-transparent px-0 py-3 text-[16px] text-charcoal placeholder:text-charcoal/45 transition-colors focus:outline-none focus:ring-0',
    invalid ? 'border-[#A3311B]' : 'border-charcoal/25 focus:border-charcoal'
)

const ContactForm: FC<ContactFormProps> = ({ serviceOptions }): ReactNode => {
    const [values, setValues] = useState(INITIAL)
    const [services, setServices] = useState<string[]>([])
    const [errors, setErrors] = useState<FieldErrors>({})
    const [status, setStatus] = useState<Status>('idle')
    const [formError, setFormError] = useState('')

    const update = (field: keyof typeof INITIAL, value: string) => {
        setValues((current) => ({ ...current, [field]: value }))

        if (errors[field as FieldName]) setErrors((current) => ({ ...current, [field]: undefined }))
    }

    const toggleService = (service: string) => {
        setServices((current) => (current.includes(service) ? current.filter((item) => item !== service) : [...current, service]))
    }

    const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault()

        const nextErrors = validate(values)

        setErrors(nextErrors)
        setFormError('')

        const firstInvalid = Object.keys(nextErrors)[0]

        if (firstInvalid) {
            document.getElementById(`contact-${firstInvalid}`)?.focus()
            return
        }

        setStatus('submitting')

        try {
            const response = await fetch('/api/contact', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ...values, services })
            })

            const data = await response.json().catch(() => null) as { ok?: boolean, message?: string, errors?: FieldErrors } | null

            if (!response.ok || !data?.ok) {
                setErrors(data?.errors ?? {})
                setFormError(data?.message ?? 'Something went wrong. Please try again.')
                setStatus('error')
                return
            }

            setStatus('success')
            setValues(INITIAL)
            setServices([])
        }
        catch {
            setFormError('We could not reach the server. Check your connection and try again.')
            setStatus('error')
        }
    }

    if (status === 'success') {
        return (
            <div role='status' className='flex min-h-[34rem] flex-col items-start justify-center animate-in fade-in duration-500'>
                <span className='flex size-16 items-center justify-center rounded-sm bg-coral'>
                    <Check aria-hidden='true' className='size-7' />
                </span>
                <h2 className='mt-8 font-heading type-section'>
                    Brief received.
                </h2>
                <p className='mt-5 max-w-md text-[17px] leading-relaxed text-charcoal/80'>
                    Thank you for getting in touch. We read every project inquiry and reply within one business day.
                </p>
                <button
                    type='button'
                    onClick={() => {
                        setStatus('idle')
                        setFormError('')
                        setErrors({})
                    }}
                    className='mt-10 inline-flex items-center gap-2 border-b border-charcoal pb-1 text-[13px] font-semibold uppercase tracking-[0.08em] transition-colors hover:text-charcoal/70'
                >
                    Send another inquiry
                    <ArrowRight aria-hidden='true' className='size-4' />
                </button>
            </div>
        )
    }

    return (
        <form noValidate onSubmit={onSubmit} aria-describedby={formError ? 'contact-form-error' : undefined}>
            <div className='grid gap-x-8 gap-y-9 sm:grid-cols-2'>
                <div>
                    <label htmlFor='contact-name' className={LABEL}>Name *</label>
                    <input
                        id='contact-name'
                        name='name'
                        type='text'
                        autoComplete='name'
                        value={values.name}
                        onChange={(event) => update('name', event.target.value)}
                        aria-invalid={Boolean(errors.name)}
                        aria-describedby={errors.name ? 'contact-name-error' : undefined}
                        placeholder='Your full name'
                        className={inputClass(Boolean(errors.name))}
                    />
                    {errors.name && <p id='contact-name-error' className={cn('mt-2 text-[13px]', ERROR)}>{errors.name}</p>}
                </div>

                <div>
                    <label htmlFor='contact-email' className={LABEL}>Email *</label>
                    <input
                        id='contact-email'
                        name='email'
                        type='email'
                        autoComplete='email'
                        value={values.email}
                        onChange={(event) => update('email', event.target.value)}
                        aria-invalid={Boolean(errors.email)}
                        aria-describedby={errors.email ? 'contact-email-error' : undefined}
                        placeholder='you@company.com'
                        className={inputClass(Boolean(errors.email))}
                    />
                    {errors.email && <p id='contact-email-error' className={cn('mt-2 text-[13px]', ERROR)}>{errors.email}</p>}
                </div>

                <div>
                    <label htmlFor='contact-company' className={LABEL}>Company name</label>
                    <input
                        id='contact-company'
                        name='company'
                        type='text'
                        autoComplete='organization'
                        value={values.company}
                        onChange={(event) => update('company', event.target.value)}
                        aria-invalid={Boolean(errors.company)}
                        aria-describedby={errors.company ? 'contact-company-error' : undefined}
                        placeholder='Company or brand'
                        className={inputClass(Boolean(errors.company))}
                    />
                    {errors.company && <p id='contact-company-error' className={cn('mt-2 text-[13px]', ERROR)}>{errors.company}</p>}
                </div>

                <div>
                    <label htmlFor='contact-budget' className={LABEL}>Budget range (optional)</label>
                    <select
                        id='contact-budget'
                        name='budget'
                        value={values.budget}
                        onChange={(event) => update('budget', event.target.value)}
                        aria-invalid={Boolean(errors.budget)}
                        className={cn(inputClass(Boolean(errors.budget)), 'cursor-pointer appearance-none bg-[length:12px] bg-[right_2px_center] bg-no-repeat pr-6', !values.budget && 'text-charcoal/55')}
                        style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 12 12' fill='none' stroke='%23252522' stroke-width='1.5'%3E%3Cpath d='M2 4l4 4 4-4'/%3E%3C/svg%3E\")" }}
                    >
                        <option value=''>Select a range</option>
                        {BUDGETS.map((budget) => (
                            <option key={budget} value={budget} className='text-charcoal'>{budget}</option>
                        ))}
                    </select>
                    {errors.budget && <p className={cn('mt-2 text-[13px]', ERROR)}>{errors.budget}</p>}
                </div>
            </div>

            {serviceOptions.length > 0 && (
                <fieldset className='mt-10'>
                    <legend className={LABEL}>Services you are interested in</legend>
                    <div className='mt-4 flex flex-wrap gap-2'>
                        {serviceOptions.map((service) => {
                            const checked = services.includes(service)

                            return (
                                <label
                                    key={service}
                                    className={cn(
                                        'inline-flex cursor-pointer select-none items-center gap-2 rounded-sm border px-3.5 py-2.5 text-[13px] font-medium transition-colors duration-200 has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-offset-2 has-[:focus-visible]:outline-coral',
                                        checked ? 'border-charcoal bg-charcoal text-cream' : 'border-charcoal/30 text-charcoal hover:border-charcoal'
                                    )}
                                >
                                    <input
                                        type='checkbox'
                                        name='services'
                                        value={service}
                                        checked={checked}
                                        onChange={() => toggleService(service)}
                                        className='sr-only'
                                    />
                                    <span aria-hidden='true' className={cn('size-2 transition-colors', checked ? 'bg-coral' : 'bg-charcoal/30')} />
                                    {service}
                                </label>
                            )
                        })}
                    </div>
                </fieldset>
            )}

            <div className='mt-10'>
                <label htmlFor='contact-message' className={LABEL}>Project description *</label>
                <textarea
                    id='contact-message'
                    name='message'
                    rows={6}
                    value={values.message}
                    onChange={(event) => update('message', event.target.value)}
                    aria-invalid={Boolean(errors.message)}
                    aria-describedby={errors.message ? 'contact-message-error' : undefined}
                    placeholder='What are you building, where do you want to go, and what would success look like?'
                    className={cn(inputClass(Boolean(errors.message)), 'resize-y')}
                />
                {errors.message && <p id='contact-message-error' className={cn('mt-2 text-[13px]', ERROR)}>{errors.message}</p>}
            </div>

            <div aria-hidden='true' className='absolute -left-[9999px] top-auto size-px overflow-hidden'>
                <label htmlFor='contact-website'>Leave this field empty</label>
                <input
                    id='contact-website'
                    name='website'
                    type='text'
                    tabIndex={-1}
                    autoComplete='off'
                    value={values.website}
                    onChange={(event) => update('website', event.target.value)}
                />
            </div>

            {formError && (
                <p id='contact-form-error' role='alert' className='mt-8 border-l-2 border-[#A3311B] bg-[#A3311B]/10 px-4 py-3 text-sm text-[#7E2312]'>
                    {formError}
                </p>
            )}

            <div className='mt-10 flex flex-col-reverse gap-5 sm:flex-row sm:items-center sm:justify-between'>
                <p className='max-w-xs text-xs leading-relaxed text-charcoal/75'>
                    We use your details only to reply to this inquiry. See our <Link href='/privacy' className='underline underline-offset-2'>privacy policy</Link>.
                </p>

                <button
                    type='submit'
                    disabled={status === 'submitting'}
                    className='group inline-flex h-14 items-center justify-center gap-3 rounded-sm bg-coral px-8 text-[13px] font-semibold uppercase tracking-[0.1em] text-charcoal transition-colors duration-300 hover:bg-charcoal hover:text-cream focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-charcoal disabled:cursor-wait disabled:opacity-70'
                >
                    {status === 'submitting' ? (
                        <>
                            <Loader2 aria-hidden='true' className='size-4 animate-spin' />
                            Sending
                        </>
                    ) : (
                        <>
                            Send inquiry
                            <ArrowRight aria-hidden='true' className='size-4 transition-transform duration-300 group-hover:translate-x-1' />
                        </>
                    )}
                </button>
            </div>
        </form>
    )
}

export default ContactForm
